import React from 'react';
import moment from 'moment';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Breadcrumb, Button, Card, Col, Row, Table, Spinner, Alert } from 'react-bootstrap';
import * as api from 'utils/api';
import Sidebar from 'components/Common/Sidebar';
import { useToastContext, Toast } from 'components/Toast';
import { useAppContext } from 'components/AppContext';
import { getLocaleDateString } from 'utils/time';
import styles from './BmsListings.module.scss';

export default function BmsListingDetails() {
  const history = useHistory();
  const { listingId: listingIdStr } = useParams();
  const listingId = parseInt(listingIdStr, 10);
  const { request } = useAppContext();
  const { toastError } = useToastContext();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['bms-listing', listingId],
    queryFn: () => api.getBmsListing(listingId),
  });

  const onImpersonate = async (ownerId, teamId) => {
    const { success, result, error: impError } = await request(api.getLoginAsUserUrl(ownerId));
    if (success) {
      try {
        const urlObj = new URL(result.url);
        const token = urlObj.searchParams.get('access_token');
        const bmsAppUrl = data?.bmsAppUrl || 'http://localhost:3004';
        const targetUrl = `${bmsAppUrl}/teams/${teamId}/listings/${listingId}?access_token=${token}`;
        window.open(targetUrl);
      } catch (err) {
        toastError('Failed to parse redirect URL from backend');
      }
    } else {
      toastError(impError || 'Failed to request login session');
    }
  };

  if (isLoading) {
    return (
      <div className="main">
        <Sidebar />
        <div className="right-part d-flex justify-content-center align-items-center">
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  if (isError || !data?.listing) {
    return (
      <div className="main">
        <Sidebar />
        <div className="right-part">
          <Alert variant="danger">
            {error?.message || 'Error loading listing details'}
          </Alert>
        </div>
      </div>
    );
  }

  const { listing, orders = [], invoices = [] } = data;

  const renderStatus = (status) => {
    let className = styles.draft;
    if (status === 'pending') className = styles.pending;
    else if (status === 'delivered') className = styles.delivered;

    return (
      <span className={`${styles.statusBadge} ${className}`}>
        {status || 'draft'}
      </span>
    );
  };

  return (
    <div className="main">
      <Sidebar />
      <div className="right-part">
        <Toast />
        <div className={styles.details}>
          <div className="title mb-3">
            <Breadcrumb>
              <Breadcrumb.Item onClick={() => history.push('/bms/listings')}>
                BMS Listings
              </Breadcrumb.Item>
              <Breadcrumb.Item active>
                {listing.customPropertyName || listing.addressLine1 || `Listing #${listingId}`}
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>

          <Row>
            {/* Basic Info Column */}
            <Col lg={8}>
              <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center bg-light">
                  <h5 className="mb-0">Basic Information</h5>
                  {renderStatus(listing.status)}
                </Card.Header>
                <Card.Body>
                  <Row className="mb-3">
                    <Col md={6}>
                      <strong>Address Line 1:</strong>
                      <p className="text-muted">{listing.addressLine1 || '—'}</p>
                    </Col>
                    <Col md={6}>
                      <strong>Address Line 2:</strong>
                      <p className="text-muted">{listing.addressLine2 || '—'}</p>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={4}>
                      <strong>City:</strong>
                      <p className="text-muted">{listing.city || '—'}</p>
                    </Col>
                    <Col md={4}>
                      <strong>State:</strong>
                      <p className="text-muted">{listing.state || '—'}</p>
                    </Col>
                    <Col md={4}>
                      <strong>Postal Code:</strong>
                      <p className="text-muted">{listing.postalCode || '—'}</p>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={4}>
                      <strong>Country:</strong>
                      <p className="text-muted">{listing.country || '—'}</p>
                    </Col>
                    <Col md={4}>
                      <strong>Property Type:</strong>
                      <p className="text-muted">{listing.propertyType || '—'}</p>
                    </Col>
                    <Col md={4}>
                      <strong>Listing Price:</strong>
                      <p className="text-muted">
                        {listing.listingPrice ? `$${listing.listingPrice.toLocaleString()}` : '—'}
                      </p>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={4}>
                      <strong>Built Area (SqFt):</strong>
                      <p className="text-muted">
                        {listing.builtArea ? `${listing.builtArea.toLocaleString()}` : '—'}
                      </p>
                    </Col>
                    <Col md={4}>
                      <strong>Lot Size:</strong>
                      <p className="text-muted">
                        {listing.lotSize ? `${listing.lotSize.toLocaleString()}` : '—'}
                      </p>
                    </Col>
                    <Col md={4}>
                      <strong>Year Built:</strong>
                      <p className="text-muted">{listing.yearBuilt || '—'}</p>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={3}>
                      <strong>Stories:</strong>
                      <p className="text-muted">{listing.stories || '—'}</p>
                    </Col>
                    <Col md={3}>
                      <strong>Bedrooms:</strong>
                      <p className="text-muted">{listing.bedrooms || '—'}</p>
                    </Col>
                    <Col md={3}>
                      <strong>Full Baths:</strong>
                      <p className="text-muted">{listing.fullBaths || '—'}</p>
                    </Col>
                    <Col md={3}>
                      <strong>Half Baths:</strong>
                      <p className="text-muted">{listing.halfBaths || '—'}</p>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={4}>
                      <strong>Parking Slots:</strong>
                      <p className="text-muted">{listing.parking || '—'}</p>
                    </Col>
                    <Col md={4}>
                      <strong>MLS Number:</strong>
                      <p className="text-muted">{listing.mlsNumber || '—'}</p>
                    </Col>
                    <Col md={4}>
                      <strong>Website Template:</strong>
                      <p className="text-muted">{listing.websiteTemplate || '—'}</p>
                    </Col>
                  </Row>

                  <div className="mb-3">
                    <strong>Description:</strong>
                    <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                      {listing.description || 'No description provided.'}
                    </p>
                  </div>

                  <div>
                    <strong>Amenities:</strong>
                    <p className="text-muted">{listing.amenities || '—'}</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Side Column: Org & Actions */}
            <Col lg={4}>
              <Card className="mb-4">
                <Card.Header className="bg-light">
                  <h5 className="mb-0">Team & Owner Details</h5>
                </Card.Header>
                <Card.Body>
                  {listing.team ? (
                    <div>
                      <div className="mb-2">
                        <strong>Team / Org Name:</strong>
                        <div>{listing.team.name}</div>
                      </div>
                      <div className="mb-2">
                        <strong>Subdomain:</strong>
                        <div>{listing.team.subdomain || '—'}</div>
                      </div>
                      {listing.team.owner ? (
                        <div className="mb-3">
                          <strong>Admin (Owner):</strong>
                          <div>{listing.team.owner.name}</div>
                          <div className="text-muted small">{listing.team.owner.email}</div>
                        </div>
                      ) : (
                        <div className="mb-3 text-warning">
                          No owner user associated with this team.
                        </div>
                      )}

                      {listing.team?.owner?.id && (
                        <Button
                          variant="primary"
                          className="w-100 mt-2"
                          onClick={() => onImpersonate(listing.team.owner.id, listing.team.id)}
                        >
                          Impersonate Admin & Open Listing
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="text-muted">No team information found.</div>
                  )}
                </Card.Body>
              </Card>

              <Card className="mb-4">
                <Card.Header className="bg-light">
                  <h5 className="mb-0">System Info</h5>
                </Card.Header>
                <Card.Body>
                  <div className="mb-2">
                    <strong>Listing ID:</strong>
                    <div>{listing.id}</div>
                  </div>
                  <div className="mb-2">
                    <strong>Creator:</strong>
                    <div>{listing.creator?.name || '—'}</div>
                    <div className="text-muted small">{listing.creator?.email || ''}</div>
                  </div>
                  <div className="mb-2">
                    <strong>Created At:</strong>
                    <div>
                      {moment.utc(listing.createdAt).local().format(`${getLocaleDateString()} HH:mm`)}
                    </div>
                  </div>
                  <div className="mb-2">
                    <strong>Updated At:</strong>
                    <div>
                      {moment.utc(listing.updatedAt).local().format(`${getLocaleDateString()} HH:mm`)}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Orders Section */}
          <Card className="mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0">{`Orders (${orders.length})`}</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Status</th>
                    <th>Total Amount</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td>{order.id}</td>
                      <td>
                        <span className="badge bg-secondary text-uppercase">{order.status || 'draft'}</span>
                      </td>
                      <td>{order.totalAmount ? `$${order.totalAmount.toLocaleString()}` : '$0'}</td>
                      <td>
                        {moment.utc(order.createdAt).local().format(`${getLocaleDateString()} HH:mm`)}
                      </td>
                    </tr>
                  ))}
                  {orders.length === 0 && (
                    <tr>
                      <td colSpan="4" className="text-center py-3 text-muted">
                        No orders associated with this listing.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Invoices Section */}
          <Card className="mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0">{`Invoices (${invoices.length})`}</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Invoice / Public ID</th>
                    <th>Status</th>
                    <th>Total Amount</th>
                    <th>Discount</th>
                    <th>Tax Group</th>
                    <th>Issued At</th>
                    <th>Due At</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>{invoice.publicId || invoice.id}</td>
                      <td>
                        <span className="badge bg-info text-uppercase">{invoice.status || 'draft'}</span>
                      </td>
                      <td>{invoice.totalAmount ? `$${invoice.totalAmount.toLocaleString()}` : '$0'}</td>
                      <td>
                        {invoice.discountAmount ? `$${invoice.discountAmount.toLocaleString()}` : '—'}
                        {invoice.discountPercentage ? ` (${invoice.discountPercentage}%)` : ''}
                      </td>
                      <td>
                        {invoice.taxGroup?.name || '—'}
                      </td>
                      <td>
                        {invoice.issuedAt
                          ? moment.utc(invoice.issuedAt).local().format(`${getLocaleDateString()} HH:mm`)
                          : '—'}
                      </td>
                      <td>
                        {invoice.dueAt
                          ? moment.utc(invoice.dueAt).local().format(getLocaleDateString())
                          : '—'}
                      </td>
                    </tr>
                  ))}
                  {invoices.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center py-3 text-muted">
                        No invoices associated with this listing.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
}
